import { getInfoByType } from 'app/utils/utils';
import { OrderParams, TargetCatalogEntryInfo, TargetCatalogSearchRequest } from 'ais-facade-api/api';
import { Row } from 'react-table';

import { Icon, Pagination, Table, Typo, VesselActionCell } from 'app/components';

import { CountryFlag } from 'CountryFlag/CountryFlag';

import { Link } from 'react-router-dom';
import i18n from 'resources/i18n';

import { useGetVesselCatalogMutation } from 'app/api/facade';
import { throttle } from 'app/helpers/throttle';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SortingInterface } from '../VesselsPage/VesselsPage';
import { useAppSelector } from 'app/store/store';

import styles from './VesselDetails.module.scss';
import clsx from 'clsx';

const columns = [
  {
    Header: <Icon name="s-ais" size="20px" color="#fff" />,
    accessor: 'type',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => {
      const ShipIcon = ({ color }: { color?: string }) =>
        vessel?.source === 'SATELLITE' ? (
          <Icon name="s-ais" size="20px" color={color} />
        ) : (
          <Icon name="t-ais" size="20px" color={color} />
        );
      const shipColor = getInfoByType(vessel.registryData?.type ?? vessel.staticData?.type)?.color;

      return (
        <div className={styles.center_icon_cell}>
          <ShipIcon color={shipColor} />
        </div>
      );
    },
  },
  {
    Header: 'MMSI',
    accessor: 'mmsi',
    cellClassName: `${styles.link}`,
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) =>
      typeof vessel.mmsi === 'number' ? (
        <Link to={`/vessels/${vessel.mmsi}`} className={styles.link_in_table}>
          {vessel.mmsi}
        </Link>
      ) : (
        <>&mdash;</>
      ),
    canSort: true,
    sortBy: 'MMSI',
  },
  {
    Header: 'Name',
    accessor: 'staticData?.name',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>
        {typeof vessel?.registryData?.name === 'string' ? (
          vessel.registryData.name
        ) : typeof vessel.staticData?.name === 'string' ? (
          vessel.staticData.name
        ) : (
          <>&mdash;</>
        )}
      </>
    ),
    canSort: true,
    sortBy: 'NAME',
  },
  {
    Header: 'IMO',
    accessor: 'staticData?.imo',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>
        {typeof vessel?.registryData?.imo === 'number' ? (
          vessel.registryData.imo
        ) : typeof vessel.staticData?.imo === 'number' ? (
          vessel.staticData.imo
        ) : (
          <>&mdash;</>
        )}
      </>
    ),
    canSort: true,
    sortBy: 'IMO',
  },
  {
    Header: 'Flag',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>
        {vessel?.meta?.countryName ? (
          <>
            {vessel.meta.countryCode && <CountryFlag countryCode={vessel.meta.countryCode.toLowerCase()} />}
            {vessel.meta.countryName} ({vessel.meta.countryCode})
          </>
        ) : (
          <>&mdash;</>
        )}
      </>
    ),
  },

  {
    Header: 'Type',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>{vessel.meta && vessel.meta.typeGroup ? vessel.meta.typeGroup : <>&mdash;</>}</>
    ),
  },
  {
    Header: 'LengthBeam',
    accessor: 'length',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => {
      const length =
        typeof vessel.registryData?.length === 'number' ? (
          <>
            {vessel.registryData?.length} {i18n.t('MeterUnit')}
          </>
        ) : (
          <>&mdash;</>
        );
      const width =
        typeof vessel.registryData?.width === 'number' ? (
          <>
            {vessel.registryData?.width} {i18n.t('MeterUnit')}
          </>
        ) : (
          <>&mdash;</>
        );

      return (
        <>
          {length} / {width}
        </>
      );
    },
  },

  {
    Header: 'CreationYear',
    accessor: 'constructionDate',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>{vessel?.registryData?.constructionDate ? new Date(vessel.registryData.constructionDate).getFullYear() : <>&mdash;</>}</>
    ),
  },
  {
    Header: 'GrossTonnage',
    accessor: 'grt',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>{vessel.registryData?.grt ? `${vessel.registryData.grt} ${i18n.t('GrossTonnageUnit')}` : <>&mdash;</>}</>
    ),
  },

  {
    Header: 'DeadWeight',
    accessor: 'dwt',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <>{vessel.registryData?.dwt ? `${vessel.registryData.dwt} ${i18n.t('WeightUnit')}` : <>&mdash;</>}</>
    ),
  },

  {
    Header: <Icon name="more-icon" width="13px" height="3px" />,
    accessor: 'actionCell',
    width: 34,
    minWidth: 34,
    maxWidth: 34,
    disableSortBy: true,
    Cell: ({ row: { original: vessel } }: { row: Row<TargetCatalogEntryInfo> }) => (
      <div className={clsx(styles.center_icon_cell, styles.action_cell)}>
        <VesselActionCell vessel={vessel} />
      </div>
    ),
  },
];

export const SimilarShipsTable = ({ mmsi }: { mmsi: number }) => {
  const initialSort: OrderParams = {
    direction: 'DESC',
    by: 'MMSI',
  };
  const user = useAppSelector((state) => state.userSlice.user);

  const [sorting, setSorting] = useState<SortingInterface>(initialSort);
  const [currentPage, setCurrentPage] = useState(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);
  const initialBody: TargetCatalogSearchRequest = {
    filter: {
      similarTo: mmsi,
    },
    order: {
      direction: 'DESC',
      by: 'MMSI',
    },
    pagination: {
      offset: 0,
      limit: 20,
    },
  };
  const [getCatalog, { data: catalog, ...catalogRequest }] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-similar-ships',
  });

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);
  useEffect(() => {
    catalogRequest.reset();
  }, [mmsi]);

  useEffect(() => {
    if (user && catalogRequest.isUninitialized) {
      debouncedGetCatalogHandler({
        ...initialBody,

        pagination: {
          offset: currentOffset,
          limit: 20,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      });
    }
  }, [catalogRequest.isUninitialized]);

  useEffect(() => {
    if (user) {
      debouncedGetCatalogHandler({
        ...initialBody,
        pagination: {
          offset: currentOffset,
          limit: 20,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      });
    }
  }, [currentOffset, user]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);

      debouncedGetCatalogHandler({
        ...initialBody,

        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      });
    }
  }, [sorting]);

  return (
    <>
      <div className={styles.table_header}>
        <Typo value={`${t('SimilarShips')}: ${typeof catalog?.total === 'number' ? catalog.total : '–'}`} size="m" />
      </div>
      {catalogRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {catalog?.results && catalog.total && catalog.results.length > 0 ? (
        <>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} columns={columns} data={catalog.results} setSorting={setSorting} />
          </div>

          <Pagination totalCount={catalog.total} currentPage={currentPage} pageChangeHandler={setCurrentPage} rowsPerPage={20} />
        </>
      ) : (
        <p className={styles.no_data}>
          {!catalogRequest.isLoading && catalog?.results?.length === 0 && `${t('NoResultsFound')}`}
        </p>
      )}
    </>
  );
};
